import { Box, Card, CardBody, Heading, SimpleGrid, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ContributingEntityContextDto } from "../../../../../autogen/bff-api";
import { LabeledValue } from "../../../../../common/components/LabeledValue";
import { StepNavigation } from "../../../../../common/StepNavigation";

interface Props {
  context: ContributingEntityContextDto;
  entityName: string;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const ContributionIntroStep = ({ context, entityName, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const deadline = useMemo(
    () => (context.bidSubmissionDeadline ? new Date(context.bidSubmissionDeadline).toLocaleString() : undefined),
    [context.bidSubmissionDeadline],
  );

  return (
    <Box pb="8" pt="6">
      <Heading size="md" mb="2">
        {t("About the procurement")}
      </Heading>
      <Text fontSize="sm" color="smMuted" mb="6">
        {t("{{leadSupplier}} has named {{entity}} as a participant in their offer to {{buyer}}.", {
          leadSupplier: context.leadSupplierName,
          entity: entityName,
          buyer: context.buyerOrganizationName,
        })}
      </Text>

      <Card variant="outline" rounded="xl" mb="6">
        <CardBody>
          <Heading size="sm" mb="4">
            {t("Procurement")}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="8" spacingY="4">
            <LabeledValue label={t("Title")} value={context.eventTitle} />
            {context.eventReference && (
              <LabeledValue label={t("Reference")} value={context.eventReference} />
            )}
            <LabeledValue label={t("Buyer")} value={context.buyerOrganizationName} />
            <LabeledValue label={t("Lead supplier")} value={context.leadSupplierName} />
            {deadline && <LabeledValue label={t("Tender deadline")} value={deadline} />}
          </SimpleGrid>
        </CardBody>
      </Card>

      <StepNavigation onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
